import { useUserEventLogger } from '@repo/tracking-service';
import { IconButton } from '@repo/ui';
import cn from 'clsx';
import { useAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';

import { autographAtom } from '@/lib/stores/power-of-attorney.ts';
import { AutographValidationResult } from '@/lib/types/power-of-attorney.ts';
import { createCanvasContext, getCanvasCoordinates, validateAutographCanvas } from '@/lib/utils/autograph.ts';

export const AutographPad = ({
  guideString,
  onValidationChange,
  disabled = false
}: {
  guideString?: string;
  onValidationChange?: (result: AutographValidationResult) => void;
  disabled?: boolean;
}) => {
  const { sendClickEvent } = useUserEventLogger();
  const [autograph, setAutograph] = useAtom(autographAtom);
  const [isPlaceholderVisible, setIsPlaceholderVisible] = useState(true);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const isDrawingRef = useRef(false);
  const hasStrokeRef = useRef(false);
  const canvasHeight = 220;

  const handleReset = () => {
    const canvas = canvasRef.current;

    if (!canvas || disabled) {
      return;
    }

    const context = createCanvasContext(canvas);

    if (!context) {
      return;
    }

    const width = canvas.getBoundingClientRect().width;

    context.clearRect(0, 0, width, canvasHeight);
    setIsPlaceholderVisible(true);
    setAutograph('');
    sendClickEvent('request_autograph_pad_refresh');
    hasStrokeRef.current = false;
    onValidationChange?.({
      isValid: false
    });
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;

    if (!canvas || !wrapper) {
      return;
    }

    const notifyValidation = () => {
      onValidationChange?.(validateAutographCanvas(canvas));
    };

    const resizeCanvas = () => {
      const width = Math.max(wrapper.clientWidth, 1);
      const ratio = window.devicePixelRatio || 1;

      canvas.width = width * ratio;
      canvas.height = canvasHeight * ratio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${canvasHeight}px`;

      const resizedContext = createCanvasContext(canvas);

      if (!resizedContext) {
        onValidationChange?.({
          isValid: false
        });
        return;
      }

      resizedContext.setTransform(ratio, 0, 0, ratio, 0, 0);
      resizedContext.clearRect(0, 0, width, canvasHeight);

      if (autograph) {
        const image = new Image();
        image.onload = () => {
          resizedContext.drawImage(image, 0, 0, width, canvasHeight);
          notifyValidation();
        };
        image.src = autograph;

        return;
      }

      notifyValidation();
    };

    resizeCanvas();

    const resizeObserver = new ResizeObserver(() => {
      resizeCanvas();
    });

    resizeObserver.observe(wrapper);

    return () => {
      resizeObserver.disconnect();
    };
  }, [canvasHeight, onValidationChange, autograph]);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return;
    }

    const completeDrawing = (pointerId?: number) => {
      if (!isDrawingRef.current) {
        return;
      }

      isDrawingRef.current = false;

      if (typeof pointerId === 'number' && canvas.hasPointerCapture(pointerId)) {
        canvas.releasePointerCapture(pointerId);
      }

      if (hasStrokeRef.current) {
        const imageDataUrl = canvas.toDataURL('image/png');
        setAutograph(imageDataUrl);
        onValidationChange?.(validateAutographCanvas(canvas));
      }
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (disabled) {
        return;
      }

      const context = createCanvasContext(canvas);

      if (!context) {
        return;
      }

      const { x, y } = getCanvasCoordinates(canvas, event);

      event.preventDefault();
      isDrawingRef.current = true;
      hasStrokeRef.current = true;
      canvas.setPointerCapture(event.pointerId);
      context.beginPath();
      context.moveTo(x, y);
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (disabled || !isDrawingRef.current) {
        return;
      }

      const context = createCanvasContext(canvas);

      if (!context) {
        return;
      }

      const { x, y } = getCanvasCoordinates(canvas, event);

      event.preventDefault();
      context.lineTo(x, y);
      context.stroke();
    };

    const handlePointerUp = (event: PointerEvent) => {
      if (disabled) {
        return;
      }

      event.preventDefault();
      completeDrawing(event.pointerId);
    };

    const handlePointerLeave = (event: PointerEvent) => {
      if (disabled) {
        return;
      }

      completeDrawing(event.pointerId);
    };

    canvas.addEventListener('pointerdown', handlePointerDown);
    canvas.addEventListener('pointermove', handlePointerMove);
    canvas.addEventListener('pointerup', handlePointerUp);
    canvas.addEventListener('pointerleave', handlePointerLeave);
    canvas.addEventListener('pointercancel', handlePointerLeave);

    return () => {
      canvas.removeEventListener('pointerdown', handlePointerDown);
      canvas.removeEventListener('pointermove', handlePointerMove);
      canvas.removeEventListener('pointerup', handlePointerUp);
      canvas.removeEventListener('pointerleave', handlePointerLeave);
      canvas.removeEventListener('pointercancel', handlePointerLeave);
    };
  }, [disabled, onValidationChange, setAutograph]);

  return (
    <div
      ref={wrapperRef}
      className={'relative overflow-hidden rounded-bzc-large bg-neutral-background-lowest'}
      onPointerDown={() => setIsPlaceholderVisible(false)}
    >
      <canvas ref={canvasRef} className="relative z-10 block w-full touch-none" />
      {(!guideString || guideString.length > 4) && isPlaceholderVisible && !autograph && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center body-medium-regular text-neutral-foreground-enabled">
          여기에 작성해주세요
        </div>
      )}
      {guideString && guideString.length < 5 && (
        <div
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-0 z-0 flex items-center justify-center text-neutral-foreground-enabled  font-light gap-normal-medium',
            {
              'text-[80px]': guideString.length < 4,
              'text-[56px]': guideString.length === 4
            }
          )}
        >
          {Array.from(guideString).map((character, index) => {
            return (
              <span key={`${character}-${index}`} className="block leading-none">
                {character === ' ' ? '\u00A0' : character}
              </span>
            );
          })}
        </div>
      )}
      {autograph && (
        <IconButton
          iconType={'arrow-counterclockwise'}
          size={'small'}
          className={'absolute bottom-normal-small right-normal-small z-20 text-neutral-foreground-lowest'}
          onClick={handleReset}
          disabled={disabled}
        />
      )}
    </div>
  );
};
